import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { ScoreDistributionChart } from "@app/components/ScoreDistributionChart";
import { fmtMs } from "@app/lib/format";
import { Trans, useLingui } from "@lingui/macro";
import {
  asInterval,
  fmtGapPoints,
  fmtHalfWidth,
  type ReferenceGap,
} from "@app/utils/reference-dataset";
import {
  AlertTriangle,
  Scale,
  BarChart3,
  CheckCircle,
  ClipboardCheck,
  Clock,
  Coins,
  Gauge,
  HelpCircle,
  Sparkles,
  Target,
  XCircle,
} from "lucide-react";

interface MetricStats {
  mean: number | null;
  median: number | null;
  min: number | null;
  max: number | null;
  std: number | null;
  count: number;
}

interface DistributionBucket {
  range_start: number;
  range_end: number;
  count: number;
}

interface PerFieldRow {
  field_key: string;
  name: string | null;
  group: string;
  evaluator_type: string | null;
  weight: number;
  n: number;
  mean_f1: number;
  mean_precision: number;
  mean_recall: number;
  exact_match_rate: number;
  n_exact_match: number;
}

interface PerGroupRow {
  group: string;
  n_fields: number;
  n_scored: number;
  mean_f1: number | null;
  mean_precision: number | null;
  mean_recall: number | null;
  exact_match_rate: number | null;
  mean_latency_ms: number | null;
  sum_latency_ms: number | null;
  n_patients_with_latency: number;
  /** 95 % interval of the group's F1, by dossier. Absent before intervals were computed. */
  f1_ci?: [number, number] | null;
}

/** One row per episode RANK — first operation, revision, second revision.
 *
 *  The rank and not the episode id: an experiment aggregates several records, and "do we score
 *  revisions worse than first operations" is the question that has an answer there. One row per id
 *  would be one row per record, which is what the runs table already is.
 *
 *  `rank: null` = the runs whose example names no act: every experiment predating this axis, and
 *  every record whose pathway was never built. */
interface PerEpisodeRow {
  rank: number | null;
  concepts: string[];
  n_runs: number;
  n: number;
  mean_f1: number | null;
  exact_match_rate: number | null;
}

interface PerEvaluatorTypeRow {
  evaluator_type: string;
  n_fields: number;
  n_scored: number;
  mean_f1: number;
  mean_precision: number;
  mean_recall: number;
  exact_match_rate: number;
}

interface LlmJudgePerFieldRow {
  field_key: string;
  name: string | null;
  evaluator_type: string | null;
  n: number;
  mean_llm_judge: number;
  mean_deterministic: number;
  delta: number;
}

interface LlmJudgeSummary {
  per_field: LlmJudgePerFieldRow[];
  mean_llm_judge: number | null;
  mean_deterministic: number | null;
  delta: number | null;
}

interface EvalBlock {
  macro_f1: number | null;
  weighted_f1: number | null;
  exact_match_rate: number | null;
  /** 95 % intervals by dossier drawn with replacement. Absent before they were computed. */
  macro_f1_ci?: [number, number] | null;
  exact_match_ci?: [number, number] | null;
  /** On a passage of a sealed reference: its macro F1 minus the work experiment's it confirms. */
  reference_gap?: ReferenceGap | null;
  error_rate: number | null;
  per_field: PerFieldRow[];
  per_group: PerGroupRow[];
  /** Absent from experiments run before the episode axis. */
  per_episode?: PerEpisodeRow[] | null;
  per_evaluator_type: PerEvaluatorTypeRow[];
  llm_judge_summary: LlmJudgeSummary | null;
  /**
   * The F1's own denominator, and what deliberately sits outside it.
   *
   * `n_scored_fields` counts the (patient × champ) pairs that carried an answer key at all —
   * the médecin validated a value, or explicitly emptied the field. `n_unvalidated_*` counts
   * the pairs where nobody answered: a score there would be invented, so the fill rate is
   * reported beside the F1 and never folded into it.
   *
   * Null on experiments run before these aggregates existed.
   */
  n_scored_fields: number | null;
  n_unvalidated_fields: number | null;
  n_unvalidated_filled: number | null;
  unvalidated_fill_rate: number | null;
  /** PROJECTION (the dated journal replayed); anything else is the retired flat aggregation. */
  scored_surface: string | null;
  n_total_merge_conflicts: number;
  n_runs_with_conflicts: number;
  latency_p50_ms: number | null;
  latency_p95_ms: number | null;
  total_wall_ms: number | null;
  llm_judge_enabled: boolean;
  label: string | null;
  tenant_id_filter: string | null;
  completed_at: string | null;
}

export interface ExperimentMetrics {
  summary: {
    total_runs: number;
    completed_runs: number;
    failed_runs: number;
    success_rate: number | null;
  };
  latency: MetricStats;
  tokens: MetricStats;
  by_key: Record<string, MetricStats>;
  score_distribution: DistributionBucket[];
  latency_distribution: DistributionBucket[];
  eval: EvalBlock | null;
}

interface ExperimentMetricsPanelProps {
  metrics: ExperimentMetrics;
}

// ─────────────────────────────────────────────────────────────────────────────
// Visual helpers — single source of truth for score → colour mapping so every
// table / badge / bar in the panel is consistent.
// ─────────────────────────────────────────────────────────────────────────────

function scoreClass(score: number | null | undefined): string {
  if (score === null || score === undefined) return "text-slate-400";
  if (score >= 0.8) return "text-green-700";
  if (score >= 0.5) return "text-primary";
  return "text-red-600";
}

function scoreBgClass(score: number | null | undefined): string {
  if (score === null || score === undefined) return "bg-slate-100 text-slate-500";
  if (score >= 0.8) return "bg-green-50 text-green-700";
  if (score >= 0.5) return "bg-blue-50 text-primary";
  return "bg-red-50 text-red-600";
}

function fmtPct(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined) return "—";
  return `${(v * 100).toFixed(digits)}%`;
}


function ScoreBar({ value }: { value: number | null | undefined }) {
  const v = value ?? 0;
  const filled = Math.max(0, Math.min(1, v));
  return (
    <div className="h-1.5 w-20 rounded-full bg-slate-100 overflow-hidden">
      <div
        className={`h-full ${
          v >= 0.8 ? "bg-green-500" : v >= 0.5 ? "bg-primary" : "bg-red-500"
        }`}
        style={{ width: `${filled * 100}%` }}
      />
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  subValue,
  color,
}: {
  icon: React.ElementType;
  label: React.ReactNode;
  value: string;
  subValue?: React.ReactNode;
  color: string;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 ${color}`} />
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      {subValue !== undefined && (
        <p className="text-xs text-slate-400 mt-0.5">{subValue}</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-panels
// ─────────────────────────────────────────────────────────────────────────────

function EvalOverview({ evalBlock }: { evalBlock: EvalBlock }) {
  const nScored = evalBlock.n_scored_fields;
  const nUnvalidated = evalBlock.n_unvalidated_fields ?? 0;
  const nUnvalidatedFilled = evalBlock.n_unvalidated_filled ?? 0;
  const macroInterval = fmtHalfWidth(asInterval(evalBlock.macro_f1_ci));
  const exactInterval = fmtHalfWidth(asInterval(evalBlock.exact_match_ci));
  const gap = evalBlock.reference_gap;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
      {gap && (
        // The number a passage exists for: how much of what was measured in work holds on dossiers
        // nothing tuned on. Said in weight, not in a hue — it is a machine state, not a verdict.
        <div className="rounded-lg border-2 border-slate-900 bg-white p-4">
          <div className="mb-1 flex items-center gap-2">
            <Scale className="h-4 w-4 text-slate-900" />
            <span className="text-xs font-semibold text-slate-700">
              <Trans>Écart avec le travail</Trans>
            </span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{fmtGapPoints(gap.gap)}</p>
          <p className="mt-0.5 text-xs">
            {gap.beyond_uncertainty === true && (
              <span className="font-semibold text-slate-900">
                <Trans>Au-delà de l'incertitude</Trans>
              </span>
            )}
            {gap.beyond_uncertainty === false && (
              <span className="text-slate-500">
                <Trans>Dans l'incertitude</Trans>
              </span>
            )}
            {gap.beyond_uncertainty === null && (
              <span className="text-slate-500">
                <Trans>Incertitude inconnue</Trans>
              </span>
            )}
            <span className="text-slate-500">
              {" "}
              · <Trans>travail : {fmtPct(gap.work_macro_f1)}</Trans>
            </span>
          </p>
        </div>
      )}
      <StatCard
        icon={Target}
        label={<Trans>Macro F1</Trans>}
        value={fmtPct(evalBlock.macro_f1)}
        subValue={
          <>
            {macroInterval && (
              <span className="mr-1 font-medium text-slate-600">
                <Trans>IC 95 % {macroInterval}</Trans> ·
              </span>
            )}
            <Trans>
              Pondéré: {fmtPct(evalBlock.weighted_f1)}
            </Trans>
          </>
        }
        color="text-primary"
      />
      <StatCard
        icon={ClipboardCheck}
        label={<Trans>Champs notés</Trans>}
        value={nScored === null || nScored === undefined ? "—" : String(nScored)}
        subValue={<Trans>paires (patient × champ) validées</Trans>}
        color="text-primary"
      />
      <StatCard
        icon={HelpCircle}
        label={<Trans>Remplissages non validés</Trans>}
        value={
          evalBlock.unvalidated_fill_rate === null ||
          evalBlock.unvalidated_fill_rate === undefined
            ? "—"
            : fmtPct(evalBlock.unvalidated_fill_rate)
        }
        subValue={
          <Trans>
            {nUnvalidatedFilled} sur {nUnvalidated} champs sans clé de réponse
          </Trans>
        }
        color="text-blue-500"
      />
      <StatCard
        icon={CheckCircle}
        label={<Trans>Taux d'exact-match</Trans>}
        value={fmtPct(evalBlock.exact_match_rate)}
        subValue={
          <>
            {exactInterval && (
              <span className="mr-1 font-medium text-slate-600">
                <Trans>IC 95 % {exactInterval}</Trans> ·
              </span>
            )}
            <Trans>par paire (patient × champ)</Trans>
          </>
        }
        color="text-green-700"
      />
      <StatCard
        icon={XCircle}
        label={<Trans>Taux d'erreur</Trans>}
        value={fmtPct(evalBlock.error_rate)}
        subValue={<Trans>patients en échec</Trans>}
        color="text-red-500"
      />
      <StatCard
        icon={AlertTriangle}
        label={<Trans>Conflits de fusion</Trans>}
        value={String(evalBlock.n_total_merge_conflicts ?? 0)}
        subValue={
          <Trans>
            sur {evalBlock.n_runs_with_conflicts ?? 0} patient(s)
          </Trans>
        }
        color="text-primary"
      />
      <StatCard
        icon={Gauge}
        label={<Trans>Latence (p50 / p95)</Trans>}
        value={`${fmtMs(evalBlock.latency_p50_ms)} / ${fmtMs(
          evalBlock.latency_p95_ms,
        )}`}
        subValue={
          <Trans>Total wall: {fmtMs(evalBlock.total_wall_ms)}</Trans>
        }
        color="text-blue-500"
      />
    </div>
  );
}

function PerFieldTable({ rows }: { rows: PerFieldRow[] }) {
  if (rows.length === 0) return null;
  // Sort by F1 ascending so failing fields surface first — that's where
  // reviewers want to look. Group label comes from ontology.group_name.
  const sorted = [...rows].sort((a, b) => a.mean_f1 - b.mean_f1);
  return (
    <Card data-tour="experiment-per-field">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          <Trans>Par champ</Trans>
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3"><Trans>Groupe</Trans></th>
              <th className="py-2 pr-3"><Trans>Champ</Trans></th>
              <th className="py-2 pr-3"><Trans>Type</Trans></th>
              <th className="py-2 pr-3 text-right"><Trans>Poids</Trans></th>
              <th className="py-2 pr-3 text-right">n</th>
              <th className="py-2 pr-3 text-right">F1</th>
              <th className="py-2 pr-3 text-right"><Trans>Précision</Trans></th>
              <th className="py-2 pr-3 text-right"><Trans>Rappel</Trans></th>
              <th className="py-2 pr-3 text-right">
                <Trans>Exact-match</Trans>
              </th>
              <th className="py-2 pr-3"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, index) => (
              <tr
                key={r.field_key}
                data-tour={index === 0 ? "experiment-weakest-field" : undefined}
                className="border-b border-slate-100 last:border-0"
              >
                <td className="py-2 pr-3 text-slate-500">{r.group}</td>
                <td className="py-2 pr-3 font-medium text-slate-800">
                  {r.name || r.field_key}
                </td>
                <td className="py-2 pr-3 text-slate-500">
                  {r.evaluator_type || "—"}
                </td>
                <td className="py-2 pr-3 text-right text-slate-500">
                  {r.weight}
                </td>
                <td className="py-2 pr-3 text-right text-slate-500">{r.n}</td>
                <td
                  className={`py-2 pr-3 text-right font-semibold ${scoreClass(r.mean_f1)}`}
                >
                  {fmtPct(r.mean_f1)}
                </td>
                <td className="py-2 pr-3 text-right text-slate-700">
                  {fmtPct(r.mean_precision)}
                </td>
                <td className="py-2 pr-3 text-right text-slate-700">
                  {fmtPct(r.mean_recall)}
                </td>
                <td className="py-2 pr-3 text-right text-slate-700">
                  {fmtPct(r.exact_match_rate)}
                </td>
                <td className="py-2 pr-3">
                  <ScoreBar value={r.mean_f1} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function PerEpisodeTable({ rows }: { rows: PerEpisodeRow[] }) {
  // Nothing to show while no act is named: a one-row « sans épisode » table reads as a failure,
  // when all it says is that this dataset was never dated.
  const named = rows.filter((r) => r.rank !== null);
  if (named.length === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          <Trans>Par épisode</Trans>
        </CardTitle>
        <p className="text-xs text-slate-500">
          <Trans>
            Chaque acte est lu au jour où il a parlé pour la dernière fois, et noté sur ce que le
            clinicien a validé de CET acte.
          </Trans>
        </p>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3"><Trans>Acte</Trans></th>
              <th className="py-2 pr-3 text-right"><Trans>Runs</Trans></th>
              <th className="py-2 pr-3 text-right"><Trans>Champs notés</Trans></th>
              <th className="py-2 pr-3 text-right">F1</th>
              <th className="py-2 pr-3 text-right">
                <Trans>Exact-match</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.rank ?? "none"} className="border-b border-slate-100 last:border-0">
                <td className="py-2 pr-3 font-medium text-slate-800">
                  {r.rank === null ? (
                    <span className="italic text-slate-400">
                      <Trans>Sans épisode</Trans>
                    </span>
                  ) : (
                    <Trans>Épisode {r.rank}</Trans>
                  )}
                </td>
                <td className="py-2 pr-3 text-right text-slate-500">{r.n_runs}</td>
                <td className="py-2 pr-3 text-right text-slate-500">{r.n}</td>
                <td className={`py-2 pr-3 text-right font-semibold ${scoreClass(r.mean_f1 ?? null)}`}>
                  {fmtPct(r.mean_f1)}
                </td>
                <td className="py-2 pr-3 text-right text-slate-700">
                  {fmtPct(r.exact_match_rate)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function PerGroupTable({ rows }: { rows: PerGroupRow[] }) {
  if (rows.length === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          <Trans>Par groupe</Trans>
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3"><Trans>Groupe</Trans></th>
              <th className="py-2 pr-3 text-right"><Trans>Champs</Trans></th>
              <th className="py-2 pr-3 text-right">F1</th>
              <th className="py-2 pr-3 text-right">
                <Trans>Exact-match</Trans>
              </th>
              <th className="py-2 pr-3 text-right">
                <Trans>Latence moy.</Trans>
              </th>
              <th className="py-2 pr-3 text-right">
                <Trans>Latence totale</Trans>
              </th>
              <th className="py-2 pr-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.group}
                className="border-b border-slate-100 last:border-0"
              >
                <td className="py-2 pr-3 font-medium text-slate-800">
                  {r.group}
                </td>
                <td className="py-2 pr-3 text-right text-slate-500">
                  {r.n_fields}
                </td>
                <td
                  className={`py-2 pr-3 text-right font-semibold ${scoreClass(r.mean_f1 ?? null)}`}
                >
                  {fmtPct(r.mean_f1)}
                  {asInterval(r.f1_ci) && (
                    <span className="ml-1 font-normal text-slate-500">
                      {fmtHalfWidth(asInterval(r.f1_ci))}
                    </span>
                  )}
                </td>
                <td className="py-2 pr-3 text-right text-slate-700">
                  {fmtPct(r.exact_match_rate)}
                </td>
                <td className="py-2 pr-3 text-right text-slate-700">
                  {fmtMs(r.mean_latency_ms)}
                </td>
                <td className="py-2 pr-3 text-right text-slate-700">
                  {fmtMs(r.sum_latency_ms)}
                </td>
                <td className="py-2 pr-3">
                  <ScoreBar value={r.mean_f1} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function PerEvaluatorTypeTable({ rows }: { rows: PerEvaluatorTypeRow[] }) {
  if (rows.length === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          <Trans>Par type d'évaluateur</Trans>
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3"><Trans>Type</Trans></th>
              <th className="py-2 pr-3 text-right"><Trans>Champs</Trans></th>
              <th className="py-2 pr-3 text-right"><Trans>Évaluations</Trans></th>
              <th className="py-2 pr-3 text-right">F1</th>
              <th className="py-2 pr-3 text-right"><Trans>Précision</Trans></th>
              <th className="py-2 pr-3 text-right"><Trans>Rappel</Trans></th>
              <th className="py-2 pr-3 text-right">
                <Trans>Exact-match</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.evaluator_type}
                className="border-b border-slate-100 last:border-0"
              >
                <td className="py-2 pr-3 font-mono text-slate-700">
                  {r.evaluator_type}
                </td>
                <td className="py-2 pr-3 text-right text-slate-500">
                  {r.n_fields}
                </td>
                <td className="py-2 pr-3 text-right text-slate-500">
                  {r.n_scored}
                </td>
                <td
                  className={`py-2 pr-3 text-right font-semibold ${scoreClass(r.mean_f1)}`}
                >
                  {fmtPct(r.mean_f1)}
                </td>
                <td className="py-2 pr-3 text-right text-slate-700">
                  {fmtPct(r.mean_precision)}
                </td>
                <td className="py-2 pr-3 text-right text-slate-700">
                  {fmtPct(r.mean_recall)}
                </td>
                <td className="py-2 pr-3 text-right text-slate-700">
                  {fmtPct(r.exact_match_rate)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function LlmJudgeSummaryPanel({ summary }: { summary: LlmJudgeSummary }) {
  const { t } = useLingui();
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <Trans>LLM-as-judge — champs textuels libres</Trans>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4 grid grid-cols-3 gap-3">
          <div className="bg-slate-50 rounded p-3">
            <p className="text-xs text-slate-500">
              <Trans>Score LLM-judge moyen</Trans>
            </p>
            <p
              className={`text-xl font-bold ${scoreClass(summary.mean_llm_judge)}`}
            >
              {fmtPct(summary.mean_llm_judge)}
            </p>
          </div>
          <div className="bg-slate-50 rounded p-3">
            <p className="text-xs text-slate-500">
              <Trans>Score déterministe moyen</Trans>
            </p>
            <p
              className={`text-xl font-bold ${scoreClass(summary.mean_deterministic)}`}
            >
              {fmtPct(summary.mean_deterministic)}
            </p>
          </div>
          <div className="bg-slate-50 rounded p-3">
            <p className="text-xs text-slate-500">
              <Trans>Écart (LLM - det.)</Trans>
            </p>
            <p
              className={`text-xl font-bold ${
                summary.delta === null
                  ? "text-slate-400"
                  : summary.delta > 0
                    ? "text-green-700"
                    : summary.delta < 0
                      ? "text-red-600"
                      : "text-slate-600"
              }`}
            >
              {summary.delta === null
                ? "—"
                : `${summary.delta > 0 ? "+" : ""}${fmtPct(summary.delta)}`}
            </p>
          </div>
        </div>

        {summary.per_field.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3"><Trans>Champ</Trans></th>
                  <th className="py-2 pr-3"><Trans>Type</Trans></th>
                  <th className="py-2 pr-3 text-right">n</th>
                  <th className="py-2 pr-3 text-right">
                    <Trans>LLM-judge</Trans>
                  </th>
                  <th className="py-2 pr-3 text-right">
                    <Trans>Déterministe</Trans>
                  </th>
                  <th className="py-2 pr-3 text-right">Δ</th>
                </tr>
              </thead>
              <tbody>
                {summary.per_field.map((r) => {
                  const isDivergent = Math.abs(r.delta) > 0.2;
                  return (
                    <tr
                      key={r.field_key}
                      className={`border-b border-slate-100 last:border-0 ${
                        isDivergent ? "bg-blue-50/50" : ""
                      }`}
                      title={
                        isDivergent
                          ? t`Écart > 20% — vérifier si l'évaluateur déterministe est trop strict ou trop laxiste`
                          : undefined
                      }
                    >
                      <td className="py-2 pr-3 font-medium text-slate-800">
                        {r.name || r.field_key}
                      </td>
                      <td className="py-2 pr-3 text-slate-500 font-mono text-xs">
                        {r.evaluator_type || "—"}
                      </td>
                      <td className="py-2 pr-3 text-right text-slate-500">
                        {r.n}
                      </td>
                      <td
                        className={`py-2 pr-3 text-right font-semibold ${scoreClass(r.mean_llm_judge)}`}
                      >
                        {fmtPct(r.mean_llm_judge)}
                      </td>
                      <td
                        className={`py-2 pr-3 text-right ${scoreClass(r.mean_deterministic)}`}
                      >
                        {fmtPct(r.mean_deterministic)}
                      </td>
                      <td
                        className={`py-2 pr-3 text-right font-semibold ${
                          r.delta > 0
                            ? "text-green-700"
                            : r.delta < 0
                              ? "text-red-600"
                              : "text-slate-500"
                        }`}
                      >
                        {r.delta > 0 ? "+" : ""}
                        {fmtPct(r.delta)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main panel
// ─────────────────────────────────────────────────────────────────────────────

export function ExperimentMetricsPanel({
  metrics,
}: ExperimentMetricsPanelProps) {
  const {
    summary,
    latency,
    tokens,
    by_key,
    score_distribution,
    latency_distribution,
    eval: evalBlock,
  } = metrics;

  return (
    <div className="space-y-6">
      {/* ── Evaluation against the validated truth ── */}
      {evalBlock && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            <Trans>Évaluation contre la vérité validée</Trans>
          </h3>
          {/*
            The rule the numbers below obey, stated once. A macro F1 has no meaning without
            the set it averages over, and that set is the clinicians' validation coverage —
            it changes from one dataset to the next while the model stays the same.
          */}
          <p className="text-xs text-slate-600 bg-blue-50 border border-slate-200 rounded-lg px-3 py-2">
            <Trans>
              Le score ne porte que sur les champs pour lesquels le médecin a
              validé une valeur — ou qu'il a explicitement vidés. Un champ jamais
              validé n'a pas de clé de réponse : il n'entre pas dans la moyenne,
              et une valeur produite pour lui est comptée à part, en
              « remplissages non validés ».
            </Trans>{" "}
            {evalBlock.scored_surface === "PROJECTION" ? (
              <span className="font-medium text-blue-900">
                <Trans>Surface mesurée : projection datée.</Trans>
              </span>
            ) : (
              <span className="font-medium text-blue-900">
                <Trans>Surface mesurée : agrégation à plat (retirée).</Trans>
              </span>
            )}
          </p>
          <EvalOverview evalBlock={evalBlock} />
          <PerFieldTable rows={evalBlock.per_field} />
          <PerGroupTable rows={evalBlock.per_group} />
          <PerEpisodeTable rows={evalBlock.per_episode ?? []} />
          <PerEvaluatorTypeTable rows={evalBlock.per_evaluator_type} />
          {evalBlock.llm_judge_summary && (
            <LlmJudgeSummaryPanel summary={evalBlock.llm_judge_summary} />
          )}
        </div>
      )}

      {/* ── Performance d'exécution ─────────────────────────────────── */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          <Trans>Performance d'exécution</Trans>
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <StatCard
            icon={BarChart3}
            label={<Trans>Runs totaux</Trans>}
            value={String(summary.total_runs)}
            color="text-primary"
          />
          <StatCard
            icon={CheckCircle}
            label={<Trans>Réussis</Trans>}
            value={String(summary.completed_runs)}
            subValue={
              summary.success_rate !== null ? (
                <Trans>
                  {(summary.success_rate * 100).toFixed(0)}% taux de réussite
                </Trans>
              ) : undefined
            }
            color="text-green-700"
          />
          <StatCard
            icon={XCircle}
            label={<Trans>Échoués</Trans>}
            value={String(summary.failed_runs)}
            color="text-red-500"
          />
          <StatCard
            icon={Clock}
            label={<Trans>Latence moy.</Trans>}
            value={fmtMs(latency.mean)}
            subValue={
              latency.median !== null ? (
                <Trans>Médiane: {fmtMs(latency.median)}</Trans>
              ) : undefined
            }
            color="text-blue-500"
          />
          <StatCard
            icon={Coins}
            label={<Trans>Tokens moy.</Trans>}
            value={
              tokens.mean !== null ? String(Math.round(tokens.mean)) : "—"
            }
            subValue={
              tokens.max !== null ? (
                <Trans>Max: {Math.round(tokens.max)}</Trans>
              ) : undefined
            }
            color="text-primary"
          />
        </div>
      </div>

      {/* ── Feedbacks (LLM-judge endpoint séparé / validation humaine) ─ */}
      {Object.keys(by_key).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              <Trans>Feedbacks (LLM-judge & validation humaine)</Trans>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(by_key).map(([key, stats]) => (
                <div
                  key={key}
                  className={`rounded-lg p-3 border ${scoreBgClass(stats.mean)}`}
                >
                  <p className="text-sm font-semibold capitalize">{key}</p>
                  <div className="mt-2 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">
                        <Trans>Moyenne</Trans>
                      </span>
                      <span className="font-medium">
                        {fmtPct(stats.mean)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">
                        <Trans>Médiane</Trans>
                      </span>
                      <span>{fmtPct(stats.median)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">
                        <Trans>Min / Max</Trans>
                      </span>
                      <span>
                        {stats.min !== null && stats.max !== null
                          ? `${fmtPct(stats.min, 0)} - ${fmtPct(stats.max, 0)}`
                          : "—"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">
                        <Trans>Évaluations</Trans>
                      </span>
                      <span>{stats.count}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Distributions ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {score_distribution.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                <Trans>Distribution des scores</Trans>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScoreDistributionChart
                data={score_distribution}
                color="#1752B7"
              />
            </CardContent>
          </Card>
        )}

        {latency_distribution.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                <Trans>Distribution des latences</Trans>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScoreDistributionChart
                data={latency_distribution}
                title=""
                color="#3b82f6"
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
